import { CampaignFlashProvider, useCampaignFlash } from './CampaignFlash.context'
import { CampaignPhase, CampaignPlanRequestDTO, CampaignStatus, ContentInventoryDTO, ContentKind } from 'models/socialCampaign'
import { CampaignSchedule, ScheduleView } from './CampaignSchedule.component'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { actionBarStateOf, firstDraftToReview, launchCountsOf, nextGenerationStart } from 'utils/socialCampaign/schedule'
import { failurePropertiesOf, trackSocialCampaignEvent } from 'utils/analytics/socialCampaign'
import {
  useEndSocialCampaign,
  useGetSocialCampaign,
  useGetSocialCampaignContentInventory,
  usePauseSocialCampaign,
  useReopenSocialCampaign,
  useResumeSocialCampaign
} from 'dataQueries/socialCampaign.query'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'
import { CampaignActionBar } from './CampaignActionBar.component'
import { CampaignConfirmDialog } from './CampaignConfirmDialog.component'
import { CampaignGenerating, CampaignGenerationAnnouncer } from './CampaignGenerating.component'
import { CampaignHeader } from './CampaignHeader.component'
import { CampaignIntro } from './CampaignIntro.component'
import { CampaignMessages } from './CampaignMessages.component'
import { CampaignPlanAndContent } from './CampaignPlanAndContent.component'
import { CampaignPostEditor } from './CampaignPostEditor.component'
import { CampaignSettingsPanel } from './CampaignSettingsPanel.component'
import CircularProgress from '@mui/material/CircularProgress'
import { ContentKindVisualsDialog } from './ContentKindVisualsDialog.component'
import { GRAY_700 } from 'constants/styling/theme'
import { GeneratePostsDialog } from './GeneratePostsDialog.component'
import { MUIButton } from 'components/common/MUIButton'
import { Path } from 'constants/router'
import { SystemMessage } from 'components/common/SystemMessage'
import { countCampaignPosts } from 'utils/socialCampaign/campaignStatus'
import { isContentKindIncluded } from 'utils/socialCampaign/planAndContent'
import styles from './ClientPropertyMarketingCampaign.module.sass'
import { useCampaignGeneration } from './useCampaignGeneration'
import { useCampaignSettings } from './useCampaignSettings'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useClientProperty } from '../_main/contexts'
import { useDynamicNow } from 'utils/hooks/useDynamicNow'
import { usePlanAndContentEditor } from './usePlanAndContentEditor'
import { usePostEditor } from './usePostEditor'
import { usePreviewAccounts } from './usePreviewAccounts'
import { useScheduleActions } from './useScheduleActions'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { VisualsChoice, visualsOfKind } from 'utils/socialCampaign/contentKindVisuals'

/** Every visual the property can feed into a campaign, and a photo of it for the intro's picture. */
const introFacts = (inventory: ContentInventoryDTO | undefined) => {
  if (!inventory) return { visualsReady: null, photoUrl: null }
  const photos = inventory.kinds.find((kind) => kind.kind === ContentKind.PHOTOS)?.items ?? []
  const photo = photos.find((item) => item.favourite) ?? photos[0]
  return {
    visualsReady: inventory.kinds.reduce((sum, kind) => sum + kind.count, 0),
    photoUrl: photo?.previewUrl ?? photo?.thumbnailUrl ?? null,
  }
}

type LifecycleAction = 'pause' | 'resume' | 'end' | 'reopen'

/**
 * The property's Marketing campaign tab (ISSUE-3621, prototype v3): no campaign yet (intro), the
 * generation, then the campaign's header and messages in every state, the networks and language
 * panel, the pause and the end with their confirmations, the reopening.
 *
 * The campaign's sections fill in under the messages: plan and content on a draft (ISSUE-3622) with the visuals
 * of each kind of content (ISSUE-3651), the schedule in every state and its action bar (ISSUE-3623): approve all
 * and launch (confirmed first on a draft, as it launches the campaign), generate new posts. A row, Review all and
 * Add a post open the post editor (ISSUE-3624), a modal with an address (`#post=…`): Back closes it, a reload
 * reopens it. The schedule's week calendar (ISSUE-3649) opens it too, on a post or in Create mode on a free hour.
 */
const MarketingCampaignTab: FC = () => {
  const { t, i18n } = useTranslation(['social_campaign'])
  const { propertyId, resolvedWorkspaceId } = useClientProperty()
  const target = useMemo(() => ({ workspaceId: resolvedWorkspaceId, realtyId: propertyId }), [propertyId, resolvedWorkspaceId])
  const previewAccounts = usePreviewAccounts(resolvedWorkspaceId)
  const clock = useDynamicNow()
  const now = useMemo(() => clock.toDate(), [clock])
  const rootRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const [focusTitle, setFocusTitle] = useState(false)

  const campaignQuery = useGetSocialCampaign(target)
  const campaign = campaignQuery.data
  const { refetch } = campaignQuery
  const refetchCampaign = useCallback(() => refetch(), [refetch])
  // refetch() cancels a read on its way (cancelRefetch defaults to true): a poll joins it instead
  const pollCampaign = useCallback(() => refetch({ cancelRefetch: false }), [refetch])

  // Once per visit of the property's tab, whichever way it was entered, as soon as the page knows whether it has a
  // campaign, or that it could not read it (the load error is what the client saw)
  const toldViewed = useRef(false)
  const loadFailed = campaign === undefined && campaignQuery.isError
  useEffect(() => {
    if (toldViewed.current || (campaign === undefined && !loadFailed)) return
    toldViewed.current = true
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_TAB_VIEWED, target, campaign ?? null, loadFailed ? { loadFailed } : {})
  }, [campaign, loadFailed, target])

  const { flash, showFlash, dismissFlash } = useCampaignFlash()

  const requestTitleFocus = useCallback(() => setFocusTitle(true), [])
  const generation = useCampaignGeneration(target, {
    language: i18n.language,
    storyStatus: campaign?.story?.status,
    refetchCampaign,
    pollCampaign,
    onDone: requestTitleFocus,
  })
  const generating = generation.step !== null

  // the intro's facts, and a draft's counts per kind of content
  const isDraft = campaign?.status === CampaignStatus.DRAFT
  const inventoryQuery = useGetSocialCampaignContentInventory(target, (campaign === null && !generating) || isDraft)
  const facts = useMemo(() => introFacts(inventoryQuery.data), [inventoryQuery.data])

  const settings = useCampaignSettings(target, campaign, refetchCampaign)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [confirming, setConfirming] = useState<'pause' | 'end' | 'launch' | null>(null)
  const [scheduleView, setScheduleView] = useState<ScheduleView>('list')

  const pause = usePauseSocialCampaign()
  const resume = useResumeSocialCampaign()
  const end = useEndSocialCampaign()
  const reopen = useReopenSocialCampaign()

  const counts = useMemo(() => campaign ? countCampaignPosts(campaign, now) : null, [campaign, now])

  // After the generation or the end (prototype v3), and when the control holding the focus goes away
  // (Reopen, Resume in the paused message, the flash's dismiss), the header's title takes the focus
  useEffect(() => {
    if (!focusTitle || !titleRef.current) return
    titleRef.current.focus()
    setFocusTitle(false)
  }, [focusTitle, generating, campaign])

  // The panel goes with a campaign that ends while it is open (a change refused because it ended in another
  // tab): closed for good, or a reopening would bring it back unasked, and the focus it held goes to the title
  const endedWhileSettingsOpen = settingsOpen && campaign?.phase === CampaignPhase.ENDED
  useEffect(() => {
    if (!endedWhileSettingsOpen) return
    setSettingsOpen(false)
    setFocusTitle(true)
  }, [endedWhileSettingsOpen])

  // A campaign launched elsewhere while its launch is asked: nothing left to confirm (its OK would get a 409), and the
  // button that opened it went with the drafts, so the title takes the focus
  const launchedWhileConfirming = confirming === 'launch' && campaign?.phase !== CampaignPhase.DRAFT
  useEffect(() => {
    if (!launchedWhileConfirming) return
    setConfirming(null)
    setFocusTitle(true)
  }, [launchedWhileConfirming])

  // A refused or failed action: say so and show the campaign as the backend has it
  const onActionError = useCallback(() => {
    showFlash({ kind: 'error' })
    void refetch()
  }, [refetch, showFlash])

  const planAndContent = usePlanAndContentEditor(target, campaign, onActionError, inventoryQuery.data)
  const schedule = useScheduleActions(target, campaign, {
    onActionError,
    refetchCampaign,
    focusTitle: requestTitleFocus,
    rootRef,
    titleRef,
    now,
  })
  const editor = usePostEditor(target, campaign, { refetchCampaign, rootRef, titleRef })
  // what holds Approve all, Launch and Generate, in the bar and in the quiet schedule
  const actionsBusy = schedule.pending || planAndContent.saving || settings.saving || editor.pending
  const { spawnSuccessToast } = useSnackbar()
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  // the kind whose visuals are open (ISSUE-3651)
  const [visualsKind, setVisualsKind] = useState<ContentKind | null>(null)
  const canUserOrder = useCanUserOrder()
  // the purchase flow for this property, in a new tab (the campaign stays where it was); ISSUE-3987 records the order
  const orderHref = canUserOrder ? `${Path.PURCHASE_FLOW}?realtyId=${encodeURIComponent(propertyId)}` : null

  /** Pause, resume, end and reopen as the analytics tell them: what was asked, from what, and how it was answered */
  const trackLifecycle = (
    action: LifecycleAction,
    facts: { heldPosts?: number, missedPosts?: number, postsAhead?: number, source?: 'switch' | 'message' } = {}
  ) => {
    const tracked = { action, ...facts }
    return {
      done: () => trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_LIFECYCLE_SUBMITTED, target, campaign, { ...tracked, outcome: 'ok' }),
      failed: (error: unknown) => trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_LIFECYCLE_SUBMITTED, target, campaign, { ...tracked, ...failurePropertiesOf(error) }),
    }
  }

  const confirmPause = () => {
    setConfirming(null)
    const tracked = trackLifecycle('pause', { heldPosts: counts?.scheduled ?? 0 })
    pause.mutate(target, {
      onSuccess: () => {
        tracked.done()
        dismissFlash()
      },
      onError: (error) => {
        tracked.failed(error)
        onActionError()
      },
    })
  }

  /** From the paused message, whose Resume button goes away with it, the focus moves to the title; the switch keeps its own */
  const doResume = ({ fromMessage = false } = {}) => {
    const missed = counts?.missedDuringPause ?? 0
    const tracked = trackLifecycle('resume', { missedPosts: missed, source: fromMessage ? 'message' : 'switch' })
    resume.mutate(target, {
      onSuccess: () => {
        tracked.done()
        showFlash({ kind: 'resumed', missed })
        if (fromMessage) setFocusTitle(true)
      },
      onError: (error) => {
        tracked.failed(error)
        onActionError()
      },
    })
  }

  /** On a draft campaign Approve all and Launch campaign launch it, its posts going out to the networks: asked first */
  const requestApproveAll = () => {
    if (campaign?.phase === CampaignPhase.DRAFT) setConfirming('launch')
    else schedule.approveAllDrafts()
  }

  const confirmLaunch = () => {
    setConfirming(null)
    schedule.approveAllDrafts()
  }

  const confirmEnd = () => {
    setConfirming(null)
    const tracked = trackLifecycle('end', { postsAhead: counts?.ahead ?? 0 })
    end.mutate(target, {
      onSuccess: () => {
        tracked.done()
        dismissFlash()
        setFocusTitle(true)
      },
      onError: (error) => {
        tracked.failed(error)
        onActionError()
      },
    })
  }

  // the ended message and its Reopen button go away: the focus moves to the title
  const doReopen = () => {
    const tracked = trackLifecycle('reopen')
    reopen.mutate(target, {
      onSuccess: () => {
        tracked.done()
        showFlash({ kind: 'reopened' })
        setFocusTitle(true)
      },
      onError: (error) => {
        tracked.failed(error)
        onActionError()
      },
    })
  }

  const closeFlash = () => {
    dismissFlash()
    setFocusTitle(true)
  }

  /**
   * The visuals dialog's Save: sent at once, the drafts are re-planned; the toast says what the campaign keeps. Leaving
   * out every visual the viewer sees leaves the kind out only when the workspace holds no other (seat-hidden orders):
   * that toast waits for the answer.
   */
  const saveVisuals = async (kind: ContentKind, choice: VisualsChoice) => {
    const answered = planAndContent.setVisuals(kind, choice)
    setVisualsKind(null)
    const total = choice.kept.length + choice.excluded.length
    const kept = t('plan.visuals.saved', { what: t(`plan.kept.${kind}`, { count: total, kept: choice.kept.length }) })
    // said once the backend took it: refused, or dropped behind a failed write, the page says so instead
    const answer = await answered
    if (!answer || !mounted.current) return
    const keepsKind = choice.kept.length > 0 || kind === ContentKind.PHOTOS || isContentKindIncluded(answer, kind)
    spawnSuccessToast(keepsKind ? kept : t(`plan.visuals.left_out.${kind}`))
  }

  const openSettings = () => {
    settings.resetFailure()
    setSettingsOpen(true)
  }

  const closeSettings = () => {
    settings.resetFailure()
    setSettingsOpen(false)
  }

  // A change still saving when the panel closed, and refused: the panel cannot say it any more, the flash does
  const { failed: settingsFailed, resetFailure: resetSettingsFailure } = settings
  useEffect(() => {
    if (!settingsFailed || settingsOpen) return
    showFlash({ kind: 'error' })
    resetSettingsFailure()
  }, [resetSettingsFailure, settingsFailed, settingsOpen, showFlash])

  const { open: openEditor } = editor
  const openPost = useCallback((postId: string) => openEditor({ kind: 'post', postId }, 'row'), [openEditor])
  const addPostFromBar = useCallback(() => openEditor({ kind: 'new', date: '', time: '' }, 'action_bar'), [openEditor])
  const addPostFromSchedule = useCallback(() => openEditor({ kind: 'new', date: '', time: '' }, 'empty_state'), [openEditor])
  const addPostAt = useCallback(({ date, time }: { date: string, time: string }) => openEditor({ kind: 'new', date, time }), [openEditor])
  // The new wave shows in the list, where its first post is brought into view (prototype v3 `gen-go`)
  const generatePosts = (plan: CampaignPlanRequestDTO) => {
    setScheduleView('list')
    schedule.generatePosts(plan)
  }
  const reviewAll = () => {
    const first = campaign ? firstDraftToReview(campaign.posts) : null
    if (first) openEditor({ kind: 'post', postId: first.id }, 'review_all')
  }

  /**
   * A way chosen in a missing kind's Add menu. Order opens the purchase flow in a new tab; Create with AI and Upload are
   * listed as prototype v3 draws them and wired by their own tickets (the reel ISSUE-3635, the staging ISSUE-3634, the
   * floor plan ISSUE-3989; upload ISSUE-3987): until then choosing one does nothing but say it was asked for.
   */
  const chooseAddWay = (way: 'create_with_ai' | 'order' | 'upload') => (kind: ContentKind) =>
    trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_CONTENT_ADD_CHOSEN, target, campaign, { contentKind: kind, way })

  let content
  if (generating) {
    content = <CampaignGenerating step={generation.step ?? 0} />
  } else if (campaign) {
    content = (
      <>
        <CampaignHeader
          ref={titleRef}
          campaign={campaign}
          now={now}
          switchBusy={pause.isPending || resume.isPending}
          onOpenSettings={openSettings}
          onPauseRequest={() => setConfirming('pause')}
          onResume={() => doResume()}
          onEndRequest={() => setConfirming('end')}
        />
        <CampaignMessages
          campaign={campaign}
          now={now}
          flash={flash}
          onDismissFlash={closeFlash}
          onResume={() => doResume({ fromMessage: true })}
          onReopen={doReopen}
          busy={resume.isPending || reopen.isPending}
        />
        {isDraft &&
          <CampaignPlanAndContent
            campaign={campaign}
            inventory={inventoryQuery.data}
            plan={planAndContent.plan}
            isIncluded={planAndContent.isIncluded}
            excludedVisuals={planAndContent.excludedVisuals}
            saving={planAndContent.saving}
            disabled={schedule.pending || editor.pending}
            onPlanChange={planAndContent.setPlan}
            onIncludedChange={planAndContent.setIncluded}
            orderHref={orderHref}
            onOrder={chooseAddWay('order')}
            onCreateWithAi={chooseAddWay('create_with_ai')}
            onUpload={chooseAddWay('upload')}
            onOpenVisuals={setVisualsKind}
          />
        }
        {isDraft && visualsKind &&
          <ContentKindVisualsDialog
            key={visualsKind}
            kind={visualsKind}
            items={visualsOfKind(inventoryQuery.data, visualsKind)}
            excludedVisuals={planAndContent.excludedVisuals}
            included={planAndContent.isIncluded(visualsKind)}
            onSave={(choice) => void saveVisuals(visualsKind, choice)}
            onClose={() => setVisualsKind(null)}
          />
        }
        <CampaignSchedule
          campaign={campaign}
          now={now}
          saving={planAndContent.saving}
          busy={actionsBusy}
          onOpenPost={openPost}
          onGenerate={schedule.openGenerate}
          onAddPost={addPostFromSchedule}
          onAddPostAt={addPostAt}
          view={scheduleView}
          onViewChange={setScheduleView}
        />
      </>
    )
  } else if (campaign === null) {
    content = <CampaignIntro visualsReady={facts.visualsReady} photoUrl={facts.photoUrl} failed={generation.failed} onStart={generation.start} />
  } else if (campaignQuery.isError) {
    content = (
      <SystemMessage
        variant="error"
        role="alert"
        title={t('load_error.title')}
        message={t('load_error.body')}
        isRawContent
        actions={<MUIButton type="secondaryBorder" size="sm" onClick={() => void refetch()}>{t('load_error.retry')}</MUIButton>}
      />
    )
  } else {
    content = <div className={styles.loading}><CircularProgress size="2.4rem" sx={{ color: GRAY_700 }} aria-label={t('loading')} /></div>
  }

  const barState = campaign && !generating ? actionBarStateOf(campaign, now) : null
  // the launch approves the drafts not approved in the editor yet; those whose time is gone or too close are skipped
  const launch = campaign ? launchCountsOf(campaign, now) : { drafts: 0, toApprove: 0 }

  return (
    <div ref={rootRef} className={styles.tab} data-bar={barState ? '' : undefined}>

      <CampaignGenerationAnnouncer step={generation.step} />

      {content}

      {barState &&
        <CampaignActionBar
          state={barState}
          busy={actionsBusy}
          onApproveAll={requestApproveAll}
          onReviewAll={reviewAll}
          onAddPost={addPostFromBar}
          onGenerate={schedule.openGenerate}
        />
      }

      {campaign && campaign.phase !== CampaignPhase.DRAFT && campaign.phase !== CampaignPhase.ENDED &&
        <GeneratePostsDialog
          open={schedule.generateOpen}
          startDay={nextGenerationStart(campaign, now)}
          pending={schedule.generating}
          onGenerate={generatePosts}
          onCancel={schedule.closeGenerate}
        />
      }

      {campaign && campaign.phase !== CampaignPhase.ENDED &&
        <CampaignSettingsPanel
          open={settingsOpen}
          onClose={closeSettings}
          draft={campaign.phase === CampaignPhase.DRAFT}
          networks={settings.networks}
          language={settings.language}
          onNetworkChange={settings.setNetwork}
          onLanguageChange={settings.setLanguage}
          failed={settings.failed}
          disabled={schedule.pending}
        />
      }

      {campaign && editor.isOpen && !generating &&
        <CampaignPostEditor campaign={campaign} target={target} now={now} editor={editor} previewAccounts={previewAccounts} />
      }

      <CampaignConfirmDialog
        open={confirming === 'pause'}
        title={t('pause.title')}
        body={t('pause.body', { count: counts?.scheduled ?? 0 })}
        confirmLabel={t('pause.confirm')}
        cancelLabel={t('pause.keep_running')}
        onConfirm={confirmPause}
        onCancel={() => setConfirming(null)}
      />

      <CampaignConfirmDialog
        open={confirming === 'end'}
        title={t('end.title')}
        body={t('end.body', { count: counts?.ahead ?? 0 })}
        confirmLabel={t('end.confirm')}
        cancelLabel={t('end.keep_running')}
        destructive
        onConfirm={confirmEnd}
        onCancel={() => setConfirming(null)}
      />

      <CampaignConfirmDialog
        open={confirming === 'launch'}
        title={launch.toApprove ? t('launch.title_approve', { count: launch.toApprove }) : t('launch.title')}
        body={t('launch.body', { count: launch.drafts })}
        confirmLabel={launch.toApprove ? t('launch.confirm_approve') : t('launch.confirm')}
        cancelLabel={t('launch.not_yet')}
        onConfirm={confirmLaunch}
        onCancel={() => setConfirming(null)}
      />

    </div>
  )
}

/** The tab behind `marketing-automation`: the page component gates it (ClientPropertyMarketingCampaign.page). */
export const ClientPropertyMarketingCampaign: FC = () => {
  const { propertyId } = useClientProperty()

  // A flash, and a plan or kind change still waiting to be saved (sent on unmount), belong to the property they were made on
  return (
    <CampaignFlashProvider key={propertyId}>
      <MarketingCampaignTab />
    </CampaignFlashProvider>
  )
}
